import BaseService from "../../../core/services/baseService";
import { getAPIBaseUrl } from "../../../environment/api";
import { PortalModule } from "../../../environment/apiEnvironment";
import { getIdToken } from "../../../utility/appUtil";
import axios from "axios";

export interface ChatMessage {
  message: string;
  session_id?: string | null;
}

export interface ChatResponse {
  status: string;
  data: {
    text: string;
    tables?: any[];
    visualizations?: Array<{
      title: string;
      image_base64: string;
    }>;
    // TODO: These fields are not currently returned by the API
    // When they become available, the feedback functionality will automatically work
    request_id?: string;
    session_id?: string;
  };
  session_id?: string;
  request_id?: string;
}

export interface FeedbackRequest {
  request_id: string;
  rating: number; // 1 for thumbs up, 0 for thumbs down
  session_id: string;
}

export interface SessionItem {
  sessionId: string;
  createdAt: string;
  updatedAt: string;
  name: string | null;
  messageCount: number;
}

export interface SessionsResponse {
  items: SessionItem[];
}

export interface SessionMessageItem {
  requestId: string;
  sessionId: string;
  userMessageContent: string;
  userMessageTimestamp: string;
  assistantResponseMetadata: {
    data: {
      text: string;
      tables: any[];
      visualizations: Array<{
        title: string;
        image_base64: string;
      }>;
    };
    error: any;
    status: string;
    timestamp: string;
    request_id: string;
    session_id: string;
  };
  assistantMessageTimestamp: string;
  createdAt: string;
  hasFeedback?: boolean;
}

export interface SessionMessagesResponse {
  items: SessionMessageItem[];
  total: number;
  limit: number;
  offset: number;
  sessionId: string;
  messageCount: number;
}

export default class GenAIService extends BaseService {
  private genaiBaseUrl: string;
  
  constructor() {
    super(getAPIBaseUrl(PortalModule.GENAI));
    // Use dev URL when running on localhost, otherwise use current origin
    this.genaiBaseUrl = window.location.hostname === 'localhost' 
      ? 'https://eu-n1-dev.nglc.net/' 
      : window.location.origin + '/';
  }

  /**
   * Send a chat message to the AI service
   * @param chatMessage - The chat message to send
   * @returns Promise<ChatResponse> - The AI response
   */
  async sendChatMessage(chatMessage: ChatMessage): Promise<ChatResponse> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    
    try {
      
      
      const response = await axios.post(`${this.genaiBaseUrl}genai/v1/chat`, chatMessage, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }

  /**
   * Send feedback for a chat response
   * @param feedback - The feedback data
   * @returns Promise<any> - The feedback response
   */
  async sendFeedback(feedback: FeedbackRequest): Promise<any> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.post(`${this.genaiBaseUrl}genai/v1/feedback`, feedback, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('GenAI Feedback Error:', error);
      throw error;
    }
  }

  /**
   * Fetch all chat sessions for the current user
   * @returns Promise<SessionsResponse> - The sessions response
   */
  async fetchSessions(): Promise<SessionsResponse> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.get(`${this.genaiBaseUrl}genai/v1/sessions`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }

  /**
   * Fetch messages for a specific chat session
   * @param sessionId - The session ID to fetch messages for
   * @returns Promise<SessionMessagesResponse> - The session messages response
   */
  async fetchSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.get(`${this.genaiBaseUrl}genai/v1/sessions/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }
      });
      
      
      return response.data;
    } catch (error: any) {
      
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }

  /**
   * Delete a specific chat session
   * @param sessionId - The session ID to delete
   * @returns Promise<any> - The delete response
   */
  async deleteSession(sessionId: string): Promise<any> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.delete(`${this.genaiBaseUrl}genai/v1/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      
      
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }

  /**
   * Update the name of a specific chat session
   * @param sessionId - The session ID to update
   * @param name - The new name for the session
   * @returns Promise<any> - The update response
   */
  async updateSessionName(sessionId: string, name: string): Promise<any> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.put(`${this.genaiBaseUrl}genai/v1/sessions/${sessionId}`, 
        { name }, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error: any) {
      
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }

  /**
   * Clear all chat sessions
   * @returns Promise<any> - The clear all response
   */
  async clearAllSessions(): Promise<any> {
    // Get the id-token for authorization
    const idToken = getIdToken();
    
    if (!idToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    try {
      
      const response = await axios.delete(`${this.genaiBaseUrl}genai/v1/sessions/clear-all`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('GenAI Clear All Sessions Error:', error);
      
      // If it's an authentication error, provide a more specific message
      if (error?.response?.status === 401) {
        throw new Error('Authentication failed. Please check your login status.');
      }
      
      throw error;
    }
  }
}
